/**
 * What a row's checks are worth, and how the notebook shows it.
 *
 * The engine gives a status per size (schema 1.5.0, `rows[].per_size[].status`): `checked` (executed against a
 * known count, errors included), `unverified` (executed, but its checks would have tested a guess: the count
 * before it was not known, or a stated count was taken on trust) and `unread` (not executed).
 *
 * Schema 1.6.0 (ADR-013, decision 6) adds what read the row (`rule_version`: « règles/1 » or « îlot/1 ») and the
 * readings found but not applied (`proposals`). The colour shown — the « tone » — derives from the three of them:
 * green only for a checked row (read by a rule, or by island once a stated count confirmed it); a row read by
 * island and not confirmed is never green; an unread row with proposals is amber; an unread row with nothing is
 * grey. A tone always comes with a glyph and a note: never colour alone.
 */
import type { Row, RowSizeResult } from '../engine/types';
import type { MessageKey } from '../i18n';

export type RowStatus = 'checked' | 'unverified' | 'unread';

/** What a count looks like: the engine status, refined by the reading layer and the proposals (1.6.0). */
export type RowTone = 'checked' | 'unverified' | 'island' | 'proposals' | 'unread';

/** Display order (legend, summary): verified first, nothing last. */
export const TONES: readonly RowTone[] = ['checked', 'unverified', 'island', 'proposals', 'unread'];

/** Weakest first: a row is only as good as its least verified size. */
const STATUS_RANK: Record<RowStatus, number> = { unread: 0, unverified: 1, checked: 2 };
const TONE_RANK: Record<RowTone, number> = { unread: 0, proposals: 1, island: 2, unverified: 3, checked: 4 };

/**
 * Glyph shown before a count (shape, not colour alone): nothing when checked, « ~ » taken on trust, « ≈ » read by
 * island and not confirmed, « ◇ » unread with proposals, « ? » unread.
 */
export const TONE_GLYPH: Record<RowTone, string> = { checked: '', unverified: '~', island: '≈', proposals: '◇', unread: '?' };

/** The reading layer of a row read by the island reader (ADR-013): « îlot/1 ». */
export const ISLAND_PREFIX = 'îlot/';

/**
 * A row read by the island reader, wholly or in part: `rule_version` « îlot/… » (1.6.0). When the result predates
 * the field (or the engine leaves it null), a translation rule « îlot/<famille> » says the same (1.5.0).
 */
export function readByIsland(row: Pick<Row, 'translation_rules' | 'rule_version'>): boolean {
  if (typeof row.rule_version === 'string') return row.rule_version.startsWith(ISLAND_PREFIX);
  return (row.translation_rules ?? []).some((rule) => rule.startsWith(ISLAND_PREFIX));
}

/** Readings the island reader found but did not apply (1.6.0), by rank. */
export function hasProposals(row: Pick<Row, 'proposals'>): boolean {
  return (row.proposals?.length ?? 0) > 0;
}

/** The engine status for one size; null when the row does not apply to the size, or when the result predates the field. */
export function cellStatus(ps: RowSizeResult | undefined): RowStatus | null {
  if (!ps || !ps.applies) return null;
  return ps.status ?? null;
}

/**
 * The tone of one size, from (status, rule_version, proposals):
 * - `checked` → green, whatever read it: the engine marks a row read by island `checked` only once the stated count
 *   that follows confirms it. Defensive: a row read by island that still carries an oracle (`exact`, `weak`,
 *   `none`: « read by island and not yet confirmed », 1.6.0) is never shown green;
 * - `unverified` → `island` when read by island, else `unverified` (taken on trust);
 * - `unread` → `proposals` when the island reader left readings, else `unread`.
 */
export function cellTone(row: Pick<Row, 'translation_rules' | 'rule_version' | 'proposals'>, ps: RowSizeResult | undefined): RowTone | null {
  const status = cellStatus(ps);
  if (status === null) return null;
  if (status === 'checked') return readByIsland(row) && ps?.oracle ? 'island' : 'checked';
  if (status === 'unverified') return readByIsland(row) ? 'island' : 'unverified';
  return hasProposals(row) ? 'proposals' : 'unread';
}

/** Engine status of a row for the selected size, or across every size it applies to (the weakest one). */
export function rowStatus(row: Row, selectedSize: number | null): RowStatus | null {
  let worst: RowStatus | null = null;
  for (const ps of row.per_size) {
    if (selectedSize !== null && ps.size !== selectedSize) continue;
    const s = cellStatus(ps);
    if (s !== null && (worst === null || STATUS_RANK[s] < STATUS_RANK[worst])) worst = s;
  }
  return worst;
}

/** Tone of a row for the selected size, or across every size it applies to (the weakest one). */
export function rowTone(row: Row, selectedSize: number | null): RowTone | null {
  let worst: RowTone | null = null;
  for (const ps of row.per_size) {
    if (selectedSize !== null && ps.size !== selectedSize) continue;
    const tone = cellTone(row, ps);
    if (tone !== null && (worst === null || TONE_RANK[tone] < TONE_RANK[worst])) worst = tone;
  }
  return worst;
}

export type ToneCounts = Record<RowTone, number>;

/** How many rows show each tone for the selected size (rows of other sizes are left out). */
export function countTones(rows: readonly Row[], selectedSize: number | null): ToneCounts {
  const counts: ToneCounts = { checked: 0, unverified: 0, island: 0, proposals: 0, unread: 0 };
  for (const row of rows) {
    const tone = rowTone(row, selectedSize);
    if (tone !== null) counts[tone] += 1;
  }
  return counts;
}

/** The tones a result shows somewhere (any size): the legend explains these, and the three of 1.5.0 always. */
export function tonesShown(rows: readonly Row[]): RowTone[] {
  const seen = new Set<RowTone>(['checked', 'unverified', 'unread']);
  for (const row of rows) for (const ps of row.per_size) {
    const tone = cellTone(row, ps);
    if (tone) seen.add(tone);
  }
  return TONES.filter((tone) => seen.has(tone));
}

/**
 * The note explaining a tone (tooltip text), or null for a count verified by a rule. A row read by island and
 * confirmed says so: green, and traceable to the island reading.
 */
export function toneNoteKey(tone: RowTone | null, island: boolean): MessageKey | null {
  switch (tone) {
    case 'checked':
      return island ? 'rows.statusIslandConfirmed' : null;
    case 'unverified':
      return 'rows.statusUnverified';
    case 'island':
      return 'rows.statusIsland';
    case 'proposals':
      return 'rows.statusProposals';
    case 'unread':
      return 'rows.statusUnread';
    default:
      return null;
  }
}
